import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import { Config } from "../../utils/config";
import { Button } from "../../components/ui/button";
import { getI18n, useTranslation } from "react-i18next";
import CouponDetailsAlert from "./CouponDetailsAlert";
import { convertHoursToObjectUpdated } from "../../utils/convert-hours-to-object-updated";
import moment from "moment";
import PromoPopupMenu from "./PromoMenu";
import ErrorBoundary from "../../ErrorBoundary";

export default function Coupons({
  couponList: data,
  currency,
  menuList,
  restaurantDetail,
  isDineIn,
  selectedType,
  orderingMethod,
  sizesOrderingMethod,
  setSelectedType,
  cartId,
  authToken,
  setCartTotal,
  id,
}: {
  couponList: any;
  currency: string;
  menuList: any;
  restaurantDetail: any;
  cartId: any;
  authToken: any;
  setCartTotal: any;
  id: any;
  isDineIn: boolean;
  selectedType: string;
  orderingMethod: string;
  sizesOrderingMethod: string;
  setSelectedType: (data: any) => void;
}) {
  const { t } = useTranslation();
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const swiperRef = useRef<any>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [hideButtons, setHideButtons] = useState(false);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<any>(null);
  // const [filteredCoupons, setFilteredCoupons] = useState<any[]>([]);
  const [activePromoFilter, setActivePromoFilter] = useState<any>(null);
  const [message, setMessage] = useState<any>(null);
  const lang = getI18n().language;

  const getNowButtonCouponsType = [
    "fixed_discount_on_selected",
    "discount_on_selected",
    "buy_one_get_one",
    "meal_bundle",
  ];

  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;

    const handleSwiperUpdate = () => {
      if (swiperInstance) {
        setIsBeginning(swiperInstance.isBeginning);
        setIsEnd(swiperInstance.isEnd);
        setHideButtons(
          swiperInstance.slides.length <= swiperInstance.params.slidesPerView
        );
      }
    };

    if (swiperInstance) {
      handleSwiperUpdate();
      swiperInstance.on("slideChange", handleSwiperUpdate);
      swiperInstance.on("reachEnd", handleSwiperUpdate);
      swiperInstance.on("reachBeginning", handleSwiperUpdate);
    }

    return () => {
      if (swiperInstance) {
        swiperInstance.off("slideChange", handleSwiperUpdate);
        swiperInstance.off("reachEnd", handleSwiperUpdate);
        swiperInstance.off("reachBeginning", handleSwiperUpdate);
      }
    };
  }, [data]);

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleLearnMore = async (item) => {
    setIsLearnMoreOpen(true);
    setSelectedCoupon(item);

    const orderType = item.details.order_type.map((type) =>
      t(`cart.ordering_method.options.${type}`)
    );

    const oncePerClient = item.details.payment_method.includes(
      "only_once_per_client"
    )
      ? t("coupon.learn_more.once_per_client")
      : "";

    const orderingTime =
      item.details.fulfillment_time?.type !== "any_time" &&
      item.details.fulfillment_time?.option?.includes(
        "days_and_time_of_the_week"
      )
        ? await convertHoursToObjectUpdated({
            hours: item.details.fulfillment_time.days_and_time_of_the_week,
            language: lang,
          })
        : "";

    const hasDateRange =
      item.details.fulfillment_time.type !== "any_time" &&
      item.details.fulfillment_time.option.includes("available_between");

    const orderingStartOrderingDate = hasDateRange
      ? moment(item.details.fulfillment_time.available_between.from).format(
          "DD/MM/YYYY"
        )
      : "";

    const orderingEndOrderingDate = hasDateRange
      ? moment(item.details.fulfillment_time.available_between.to).format(
          "DD/MM/YYYY"
        )
      : "";

    const minOrder =
      item.details.is_min_order && item.details.min_order
        ? `${t("coupon.learn_more.sub_total").replace(
            "{amount}",
            item.details.min_order
          )} ${currency}`
        : "";

    const clientType =
      item.details.client_type === "only_returning_clients"
        ? t("coupon.learn_more.returning_clients")
        : item.details.client_type === "only_new_clients"
        ? t("coupon.learn_more.new_clients")
        : "";

    const commonDescription = [
      orderType.length > 0
        ? `${t("coupon.learn_more.order_type")} ${orderType.join(", ")}`
        : "",
      oncePerClient
        ? `${t(
            item.type.includes("selected")
              ? "coupon.learn_more.once_per_client"
              : "coupon.learn_more.once_per_client_order"
          )}`
        : "",
      orderingTime
        ? `${t("coupon.learn_more.ordering_time")} ${orderingTime
            .map((item) => `${t(item.day)} ${item.hours}`)
            .join("; ")}`
        : "",
      orderingStartOrderingDate && orderingEndOrderingDate
        ? `${t(
            "coupon.learn_more.ordering_date"
          )} ${orderingStartOrderingDate} - ${orderingEndOrderingDate}`
        : "",
      minOrder,
      clientType,
    ].filter((item) => item !== "");

    let whatYouGet = "";
    switch (item.type) {
      case "discount_on_selected":
        whatYouGet = t("coupon.learn_more.discount_on_specific_item").replace(
          "{amount}",
          item.details.discount
        );
        break;
      case "free_delivery":
        whatYouGet = t("coupon.learn_more.free_delivery").replace(
          "{amount}",
          item.details.discount
        );
        break;
      case "fixed_discount":
        whatYouGet = t("coupon.learn_more.fixed_discount_on_cart").replace(
          "{amount}",
          item.details.discount + ` ${currency}`
        );
        break;
      case "discount_on_cart":
        whatYouGet = t("coupon.learn_more.discount_on_cart").replace(
          "{amount}",
          item.details.discount
        );
        break;
      case "buy_one_get_one":
        whatYouGet = t("coupon.learn_more.bogo_what_you_get").replace(
          "{amount}",
          item.details.item_group_2_or_most_expensive_item_discount ||
            item.details.item_group_1_or_cheaper_item_discount
        );
        break;
      case "meal_bundle":
        whatYouGet = t("coupon.learn_more.meal_bundle").replace(
          "{amount}",
          item.details.discount + ` ${currency}`
        );
        break;
      case "fixed_discount_on_selected":
        whatYouGet = t("coupon.learn_more.fixed_discount_on_selected").replace(
          "{amount}",
          item.details.discount + ` ${currency}`
        );
        break;
      default:
        whatYouGet = "";
    }

    setMessage([
      {
        title: t("coupon.learn_more.what_you_get"),
        description: [whatYouGet],
      },
      commonDescription.length > 0 && {
        title: t("coupon.learn_more.conditions"),
        description: commonDescription,
      },
    ]);
  };

  const handleGetItNow = (item: any) => {
    setIsPromoOpen(false);
    setTimeout(() => {
      setSelectedPromo(item);
      setIsPromoOpen(true);
      setActivePromoFilter(item);
      setIsLearnMoreOpen(false);
    }, 50);
  };

  const clearPromoFilter = () => {
    setActivePromoFilter(null);
  };

  return (
    <div className="relative w-full">
      <>
        <div className="flex-shrink-0 flex items-center gap-2">
          {isBeginning && isEnd ? (
            <></>
          ) : (
            <div
              className={`${
                hideButtons ? "hidden" : "flex"
              } items-center gap-0.5`}
            >
              <button
                onClick={handlePrev}
                className="absolute top-1/2 -translate-y-1/2 left-1 sm:left-2 z-[3] bg-white rounded-full p-1 sm:p-2 disabled:opacity-50"
                disabled={isBeginning}
              >
                <ChevronLeft className="w-3 sm:w-4 h-3 sm:h-4 stroke-2 text-black" />
              </button>
              <button
                onClick={handleNext}
                className="absolute top-1/2 -translate-y-1/2 right-1 sm:right-2 z-[3] bg-white rounded-full p-1 sm:p-2 disabled:opacity-50"
                disabled={isEnd}
              >
                <ChevronRight className="w-3 sm:w-4 h-3 sm:h-4 stroke-2 text-black" />
              </button>
            </div>
          )}
        </div>

        <div className="max-w-full">
          <Swiper
            ref={swiperRef}
            spaceBetween={16}
            breakpoints={{
              0: {
                spaceBetween: 12,
              },
              640: {
                spaceBetween: 16,
              },
            }}
            slidesPerView={"auto"}
            className="max-w-full"
            wrapperClass="max-w-full"
          >
            {data.map((item: any, index: number) => (
              <ErrorBoundary>
                <SwiperSlide key={index} className="!w-[248px] sm:!w-[328px]">
                  <div
                    key={item.id}
                    className="relative flex rounded-lg overflow-hidden z-[1]"
                  >
                    <div className="min-h-28 sm:min-h-40 h-full w-full p-3 bg-black/40 space-y-2">
                      <div className="text-sm sm:text-lg text-white line-clamp-2 break-all">
                        {item.name}
                      </div>
                      {item.description && (
                        <div className="text-xs sm:text-base text-white line-clamp-2 break-all">
                          {item.description}
                        </div>
                      )}
                    </div>
                    {item?.details?.image && (
                      <img
                        className="absolute inset-0 z-[-1] h-full w-full object-cover"
                        src={`${
                          Config.env.app.image_url
                        }/${item?.details?.image.replace("//", "/")}`}
                        alt=""
                        width={100}
                        height={100}
                      />
                    )}
                    {getNowButtonCouponsType.includes(item.type) && (
                      <div className="absolute bottom-2 right-2">
                        <Button
                          variant="coupon"
                          className="w-full"
                          onClick={() => handleGetItNow(item)}
                        >
                          {t("coupon.get_it_now")}
                        </Button>
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2">
                      <Button
                        variant="ghost"
                        className="w-full text-white underline"
                        onClick={() => handleLearnMore(item)}
                      >
                        {t("coupon.learn_more.title")}
                      </Button>
                    </div>
                  </div>
                </SwiperSlide>
              </ErrorBoundary>
            ))}
          </Swiper>
        </div>
      </>

      {activePromoFilter && menuList && (
        <ErrorBoundary>
          <PromoPopupMenu
            isOpen={isPromoOpen && !!selectedPromo}
            setOpen={setIsPromoOpen}
            menuList={menuList}
            selectedPromo={selectedPromo}
            restaurantDetail={restaurantDetail}
            orderingMethod={orderingMethod}
            sizesOrderingMethod={sizesOrderingMethod}
            selectedType={selectedType}
            isDineIn={isDineIn}
            setSelectedType={setSelectedType}
            cartId={cartId}
            id={id}
            authToken={authToken}
            setCartTotal={setCartTotal}
          />
        </ErrorBoundary>
      )}

      <CouponDetailsAlert
        promoType={selectedCoupon?.type}
        open={isLearnMoreOpen}
        setOpen={setIsLearnMoreOpen}
        message={message}
        title={selectedCoupon?.name}
        getItNow={() => {
          if (selectedCoupon) {
            handleGetItNow(selectedCoupon);
          }
        }}
        getNowButtonCouponsType={getNowButtonCouponsType}
      />
    </div>
  );
}
